import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Pill, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  Check,
  Sun,
  Moon,
  Sunrise,
  Edit3,
  X
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Orari comuni pre-configurati
const ORARI_COMUNI = [
  { label: '07:00', value: '07:00' },
  { label: '08:00', value: '08:00' },
  { label: '09:00', value: '09:00' },
  { label: '12:00', value: '12:00' },
  { label: '13:00', value: '13:00' },
  { label: '14:00', value: '14:00' },
  { label: '18:00', value: '18:00' },
  { label: '19:00', value: '19:00' },
  { label: '20:00', value: '20:00' },
  { label: '21:00', value: '21:00' },
  { label: '22:00', value: '22:00' },
];

// Componente per la selezione rapida degli orari
const OrarioSelector = ({ selectedOrari, onToggle }) => {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-700">Seleziona gli orari di somministrazione:</p>
      <div className="flex flex-wrap gap-2">
        {ORARI_COMUNI.map(orario => {
          const isSelected = selectedOrari.includes(orario.value);
          const fascia = getFascia(orario.value);
          const fasciaColors = {
            'MATTINA': 'bg-amber-100 border-amber-300 text-amber-700',
            'POMERIGGIO': 'bg-orange-100 border-orange-300 text-orange-700',
            'SERA': 'bg-indigo-100 border-indigo-300 text-indigo-700'
          };
          
          return (
            <button
              key={orario.value}
              onClick={() => onToggle(orario.value)}
              className={`px-4 py-2 rounded-xl border-2 font-medium text-sm transition-all ${
                isSelected 
                  ? `${fasciaColors[fascia]} ring-2 ring-offset-1 ring-${fascia === 'MATTINA' ? 'amber' : fascia === 'POMERIGGIO' ? 'orange' : 'indigo'}-400`
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {orario.label}
              {isSelected && <Check className="w-3 h-3 ml-1 inline" />}
            </button>
          );
        })}
      </div>
      
      {/* Orario personalizzato */}
      <div className="flex items-center gap-2 mt-3">
        <input
          type="time"
          className="px-3 py-2 border-2 border-gray-200 rounded-xl text-sm"
          onChange={(e) => {
            if (e.target.value && !selectedOrari.includes(e.target.value)) {
              onToggle(e.target.value);
              e.target.value = '';
            }
          }}
        />
        <span className="text-sm text-gray-500">Aggiungi orario personalizzato</span>
      </div>
    </div>
  );
};

// Helper per determinare la fascia
const getFascia = (orario) => {
  const hour = parseInt(orario.split(':')[0]);
  if (hour >= 0 && hour < 12) return 'MATTINA';
  if (hour >= 12 && hour < 18) return 'POMERIGGIO';
  return 'SERA';
};

const getFasciaIcon = (fascia) => {
  switch(fascia) {
    case 'MATTINA': return <Sunrise className="w-4 h-4" />;
    case 'POMERIGGIO': return <Sun className="w-4 h-4" />;
    case 'SERA': return <Moon className="w-4 h-4" />;
    default: return <Clock className="w-4 h-4" />;
  }
};

const getFasciaStyle = (fascia) => {
  switch(fascia) {
    case 'MATTINA': return 'bg-amber-50 border-amber-200 text-amber-700';
    case 'POMERIGGIO': return 'bg-orange-50 border-orange-200 text-orange-700';
    case 'SERA': return 'bg-indigo-50 border-indigo-200 text-indigo-700';
    default: return 'bg-gray-50 border-gray-200 text-gray-700';
  }
};

// Componente principale Step 2 - Medicine Setup
const MedicineSetupStep = ({ famigliaId, onComplete, onSkip, initialMedicines = [] }) => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form nuovo farmaco
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMedicine, setNewMedicine] = useState({
    nome_farmaco: '',
    dosaggio: '',
    orari: [],
    note: ''
  });
  
  // Carica medicine esistenti
  const loadMedicines = useCallback(async () => {
    if (!famigliaId) {
      // Se non c'è ancora famiglia_id, smetti di fare loading
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/medicine/schedule/${famigliaId}`);
      if (res.ok) {
        const data = await res.json();
        setMedicines(data.schedule || []);
      }
    } catch (err) {
      console.error('Error loading medicines:', err);
    } finally {
      setLoading(false);
    }
  }, [famigliaId]);
  
  useEffect(() => {
    loadMedicines();
  }, [loadMedicines]);
  
  // Toggle orario nella selezione
  const toggleOrario = (orario) => {
    setNewMedicine(prev => ({
      ...prev,
      orari: prev.orari.includes(orario)
        ? prev.orari.filter(o => o !== orario)
        : [...prev.orari, orario].sort()
    }));
  };
  
  // Aggiungi nuovo farmaco
  const handleAddMedicine = async () => {
    if (!newMedicine.nome_farmaco.trim()) {
      toast.error('Inserisci il nome del farmaco');
      return;
    }
    
    if (newMedicine.orari.length === 0) {
      toast.error('Seleziona almeno un orario');
      return;
    }
    
    if (!famigliaId) {
      toast.error('Errore: ID famiglia non trovato. Riprova.');
      console.error('Missing famigliaId in handleAddMedicine');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/medicine/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          famiglia_id: famigliaId,
          nome_farmaco: newMedicine.nome_farmaco,
          dosaggio: newMedicine.dosaggio || null,
          orari: newMedicine.orari,
          note: newMedicine.note || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.count} dosi aggiunte!`);
        setNewMedicine({ nome_farmaco: '', dosaggio: '', orari: [], note: '' });
        setShowAddForm(false);
        loadMedicines();
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Save medicine error:', errorData);
        throw new Error(errorData.detail || 'Errore salvataggio');
      }
    } catch (err) {
      console.error('handleAddMedicine error:', err);
      toast.error('Errore nel salvataggio: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Elimina farmaco
  const handleDeleteMedicine = async (medicineId) => {
    try {
      const res = await fetch(`${API_URL}/api/medicine/schedule/${medicineId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Farmaco rimosso');
        loadMedicines();
      }
    } catch (err) {
      toast.error('Errore eliminazione');
    }
  };
  
  // Raggruppa per fascia
  const grouped = {
    MATTINA: medicines.filter(m => m.fascia_oraria === 'MATTINA'),
    POMERIGGIO: medicines.filter(m => m.fascia_oraria === 'POMERIGGIO'),
    SERA: medicines.filter(m => m.fascia_oraria === 'SERA')
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-[#4A7C59] border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-[#4A7C59]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Pill className="w-8 h-8 text-[#4A7C59]" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">Piano Farmaci</h2>
        <p className="text-gray-500 mt-1">Configura i farmaci e gli orari di somministrazione</p>
      </div>
      
      {/* Riepilogo fasce */}
      {medicines.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {['MATTINA', 'POMERIGGIO', 'SERA'].map(fascia => (
            <div key={fascia} className={`p-4 rounded-xl border-2 text-center ${getFasciaStyle(fascia)}`}>
              <div className="flex items-center justify-center gap-2 mb-1">
                {getFasciaIcon(fascia)}
                <span className="font-bold text-lg">{grouped[fascia].length}</span>
              </div>
              <p className="text-xs">{fascia}</p>
            </div>
          ))}
        </div>
      )}
      
      {/* Lista farmaci per fascia */}
      {medicines.length > 0 && (
        <div className="space-y-4">
          {['MATTINA', 'POMERIGGIO', 'SERA'].map(fascia => (
            grouped[fascia].length > 0 && (
              <div key={fascia} className={`rounded-xl border-2 overflow-hidden ${getFasciaStyle(fascia)}`}>
                <div className="px-4 py-2 flex items-center gap-2 border-b border-current/20">
                  {getFasciaIcon(fascia)}
                  <span className="font-bold">{fascia}</span>
                  <span className="text-sm opacity-70">
                    ({fascia === 'MATTINA' ? '00:00-12:00' : fascia === 'POMERIGGIO' ? '12:00-18:00' : '18:00-24:00'})
                  </span>
                </div>
                <div className="bg-white/80 divide-y divide-gray-100">
                  {grouped[fascia].map(med => (
                    <div key={med.id} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{med.nome_farmaco}</p>
                        <p className="text-sm text-gray-500">
                          {med.dosaggio && `${med.dosaggio} • `}
                          ore {med.orario?.slice(0, 5)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteMedicine(med.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}
      
      {/* Form aggiunta farmaco */}
      {showAddForm ? (
        <div className="bg-white rounded-2xl border-2 border-[#4A7C59] p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Nuovo Farmaco</h3>
            <button onClick={() => setShowAddForm(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Farmaco *
              </label>
              <input
                type="text"
                value={newMedicine.nome_farmaco}
                onChange={(e) => setNewMedicine({ ...newMedicine, nome_farmaco: e.target.value })}
                placeholder="es. Tachipirina"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#4A7C59] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dosaggio
              </label>
              <input
                type="text"
                value={newMedicine.dosaggio}
                onChange={(e) => setNewMedicine({ ...newMedicine, dosaggio: e.target.value })}
                placeholder="es. 1 compressa"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#4A7C59] focus:outline-none"
              />
            </div>
          </div>
          
          {/* Selezione orari */}
          <OrarioSelector 
            selectedOrari={newMedicine.orari}
            onToggle={toggleOrario}
          />
          
          {/* Preview fasce selezionate */}
          {newMedicine.orari.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-sm text-gray-600 mb-2">
                <strong>{newMedicine.orari.length}</strong> dosi selezionate:
              </p>
              <div className="flex flex-wrap gap-2">
                {newMedicine.orari.sort().map(orario => {
                  const fascia = getFascia(orario);
                  return (
                    <span key={orario} className={`px-3 py-1 rounded-full text-sm font-medium ${getFasciaStyle(fascia)}`}>
                      {getFasciaIcon(fascia)} {orario} 
                      <button 
                        onClick={() => toggleOrario(orario)}
                        className="ml-1 hover:text-red-500"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (opzionale)
            </label>
            <input
              type="text"
              value={newMedicine.note}
              onChange={(e) => setNewMedicine({ ...newMedicine, note: e.target.value })}
              placeholder="es. Da assumere dopo i pasti"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#4A7C59] focus:outline-none"
            />
          </div>
          
          {/* Bottone salva */}
          <Button
            onClick={handleAddMedicine}
            disabled={saving || !newMedicine.nome_farmaco || newMedicine.orari.length === 0}
            className="w-full bg-[#4A7C59] hover:bg-[#3d6549] py-6 text-lg"
          >
            {saving ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Salva {newMedicine.orari.length > 1 ? `(${newMedicine.orari.length} dosi)` : 'Farmaco'}
              </>
            )}
          </Button>
        </div>
      ) : (
        <Button
          onClick={() => setShowAddForm(true)}
          variant="outline"
          className="w-full py-6 border-2 border-dashed border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59]/5"
        >
          <Plus className="w-5 h-5 mr-2" />
          Aggiungi Farmaco
        </Button>
      )}
      
      {/* Bottone continua - se ci sono farmaci salvati */}
      {medicines.length > 0 && onComplete && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>✅ Farmaci configurati!</strong><br/>
              Ora invia il link all'assistente per permetterle di usare l'app e registrare le somministrazioni.
            </p>
          </div>
          <Button
            onClick={onComplete}
            className="w-full bg-[#4A7C59] hover:bg-[#3d6549] py-6 text-lg"
          >
            Vai all'invio link assistente
            <span className="ml-2">→</span>
          </Button>
        </>
      )}
      
      {/* Bottone Salta - se NON ci sono farmaci salvati */}
      {medicines.length === 0 && onSkip && (
        <>
          <Button
            onClick={onSkip}
            className="w-full bg-[#4A7C59] hover:bg-[#3d6549] py-6 text-lg"
          >
            Salta e Continua
            <span className="ml-2">→</span>
          </Button>
          <p className="text-center text-sm text-gray-500">
            Puoi aggiungere i farmaci anche in seguito dalla dashboard
          </p>
        </>
      )}
    </div>
  );
};

export default MedicineSetupStep;
